import React from 'react';
import { IconButton, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText, type IconButtonProps } from '@mui/material';
import AppsIcon from '@mui/icons-material/Apps';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { APPS, AppConfig } from './apps.config';

export type AppSwitcherProps = {
  currentAppId?: string;
  apps?: AppConfig[];
  title?: string;
  buttonColor?: IconButtonProps['color'];
};

const normalizeHost = (url: string): string => {
  try {
    const u = new URL(url, window.location.origin);
    return `${u.protocol}//${u.host}`;
  } catch {
    return url;
  }
};

const resolveAppsHubUrl = (): string => {
  const prodDefault = 'https://apps.optimauitzendburo.nl';

  try {
    // Prefer explicit env override if present
    // @ts-ignore
    const envUrl =
      (typeof import.meta !== 'undefined' ? (import.meta.env?.VITE_APP_URL_APPS as string | undefined) : undefined) ||
      (typeof process !== 'undefined' ? (process.env?.VITE_APP_URL_APPS as string | undefined) : undefined);
    if (envUrl) return envUrl;
  } catch {
    // ignore
  }

  try {
    if (typeof window !== 'undefined') {
      const host = window.location.hostname;
      if (host === 'localhost' || host === '127.0.0.1') {
        return 'http://localhost:5177';
      }
    }
  } catch {
    // ignore
  }

  return prodDefault;
};

export const AppSwitcher: React.FC<AppSwitcherProps> = ({ currentAppId, apps, title, buttonColor }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const resolvedButtonColor = buttonColor ?? 'primary';

  const list = (apps || APPS).filter(a => a.url);
  const appsHubUrl = React.useMemo(resolveAppsHubUrl, []);

  // If currentAppId missing, try infer from location
  const inferredId = React.useMemo(() => {
    const here = `${window.location.protocol}//${window.location.host}`;
    const match = list.find(a => normalizeHost(a.url) === here);
    return match?.id;
  }, [list]);

  const current = currentAppId || inferredId;

  const openInNewTab = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const switcherButton = (
    <IconButton color={resolvedButtonColor} onClick={handleOpen} size="small" aria-label="Switch application">
      <AppsIcon />
    </IconButton>
  );

  return (
    <>
      <Tooltip title={title || 'Switch application'} children={switcherButton} />
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        {appsHubUrl ? (
          <MenuItem
            key="apps-hub"
            onClick={() => {
              handleClose();
              openInNewTab(appsHubUrl);
            }}
          >
            <ListItemIcon>
              <AppsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Apps Hub</ListItemText>
          </MenuItem>
        ) : null}
        {list.map(app => (
          <MenuItem
            key={app.id}
            selected={app.id === current}
            onClick={() => {
              handleClose();
              openInNewTab(app.url);
            }}
          >
            <ListItemIcon>
              <OpenInNewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{app.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default AppSwitcher;
